import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiActivity, FiServer, FiAlertOctagon, FiCheckSquare, FiSettings, FiDownload, FiBell, FiSearch, FiMenu, FiPlayCircle } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export const Layout = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
      alert('Posture Report Generated Successfully.');
    }, 1500);
  };

  const navItems = [
    { path: '/', icon: FiActivity, label: 'Command Center' },
    { path: '/assets', icon: FiServer, label: 'Asset Mesh' },
    { path: '/alerts', icon: FiAlertOctagon, label: 'Alert Queue' },
    { path: '/compliance', icon: FiCheckSquare, label: 'Compliance' },
    { path: '/demo', icon: FiPlayCircle, label: 'Demo' },
    { path: '/settings', icon: FiSettings, label: 'Settings' },
  ];

  const NavContent = () => (
    <>
      <div className="p-6">
        <div className="flex items-center gap-3 text-emerald-400 font-bold text-xl tracking-wider">
          <SafeIcon icon={FiActivity} className="text-2xl" />
          <span>CYBERLOOM</span>
        </div>
        <div className="mt-2 text-xs text-slate-500 font-mono">POSTURE MONITORING v2.4</div>
      </div>

      <nav className="flex-1 px-4 space-y-2 mt-4 overflow-y-auto custom-scrollbar">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setMobileMenuOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                isActive
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                  : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`
            }
          >
            <SafeIcon icon={item.icon} className="text-lg" />
            <span className="font-medium">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-slate-200 font-sans selection:bg-emerald-500/30 overflow-hidden flex relative">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/5 rounded-full blur-[120px] pointer-events-none" />

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      <aside className="hidden md:flex w-64 flex-col bg-black/60 backdrop-blur-xl border-r border-white/10 z-10 relative">
        <NavContent />
      </aside>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
            className="md:hidden fixed inset-y-0 left-0 w-64 flex flex-col bg-[#0f0f0f] border-r border-white/10 z-50 shadow-2xl"
          >
            <NavContent />
          </motion.aside>
        )}
      </AnimatePresence>

      <main className="flex-1 flex flex-col h-screen overflow-hidden relative z-10">
        <header className="h-20 bg-black/40 backdrop-blur-md border-b border-white/10 flex items-center justify-between px-4 md:px-8 shrink-0">
          <div className="flex items-center gap-4">
            <button 
              className="md:hidden p-2 text-slate-400 hover:text-white"
              onClick={() => setMobileMenuOpen(true)}
            >
              <SafeIcon icon={FiMenu} className="text-2xl" />
            </button>
            <div className="hidden md:flex items-center bg-white/5 border border-white/10 rounded-lg px-4 py-2 w-64 focus-within:border-emerald-500/50 transition-colors">
              <SafeIcon icon={FiSearch} className="text-slate-500 mr-2" />
              <input
                type="text"
                placeholder="Search assets, policies..."
                className="bg-transparent border-none outline-none text-sm w-full placeholder-slate-600 focus:ring-0"
              />
            </div>
          </div>

          <div className="flex items-center gap-4 md:gap-6">
            <button className="relative text-slate-400 hover:text-white transition-colors">
              <SafeIcon icon={FiBell} className="text-xl" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50"
            >
              {isExporting ? (
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
                  <SafeIcon icon={FiActivity} />
                </motion.div>
              ) : (
                <SafeIcon icon={FiDownload} />
              )}
              <span className="hidden sm:inline">{isExporting ? 'Generating...' : 'Export Report'}</span>
            </button>
            <div className="w-8 h-8 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-sm font-bold text-slate-300">
              IT
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-4 md:p-8 custom-scrollbar">
          <Outlet />
        </div>
        
        <footer className="text-center py-2 text-[10px] text-slate-600 font-mono tracking-widest border-t border-white/5 bg-black/20">
          Made by LingLink AI and DigLab AI
        </footer>
      </main>
    </div>
  );
};